import React, { createContext, useState, useContext, useEffect } from "react";
import { GET } from "../service/CRUDService";
import EntityConstants from "../configs/EntityConstants";
import i18n from "../i18n/i18n";
import { appConfig } from "../configs/tenantConfig";

export const pickDefaultLanguage = (
  availableLangs,
  preferredLocale = appConfig.preferredLocale,
) =>
  preferredLocale && availableLangs.includes(preferredLocale)
    ? preferredLocale
    : availableLangs[0] || "en";

const BrandingContext = createContext({
  organisationData: null,
  isLoading: true,
  error: null,
  refreshBranding: () => {},
});

export const BrandingContextProvider = ({ children }) => {
  const [organisationData, setOrganisationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrganisationData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await GET(EntityConstants.GET_ORGANISATION_DETAILS);
      if (!res?.ok) {
        let errorBody = null;
        try {
          errorBody = await res.json();
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(
          errorBody?.message ||
            `Branding fetch failed (${res?.status ?? "unknown status"})`,
        );
      }
      const data = await res.json();
      setOrganisationData(data);

      if (data.languages) {
        const availableLangs = Object.keys(data.languages);
        const savedLang = localStorage.getItem("selectedLanguage");
        if (!savedLang || !availableLangs.includes(savedLang)) {
          const defaultLang = pickDefaultLanguage(availableLangs);
          i18n.changeLanguage(defaultLang);
          localStorage.setItem("selectedLanguage", defaultLang);
        }
      }

      if (window.setupBranding) {
        window.setupBranding(data);
      }

      return data;
    } catch (err) {
      console.log("Branding API failed", err);
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  const refreshBranding = () => {
    return fetchOrganisationData();
  };

  const value = {
    organisationData,
    isLoading,
    error,
    refreshBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within BrandingContextProvider");
  }
  return context;
};

export default BrandingContext;
